export class _lightbox {
    modal: boolean;
    target: HTMLElement;
    el: {
        cover: HTMLElement | string;
        main: HTMLElement | string;
        btn: {
            close: HTMLElement | string;
        };
    };
    content: {
        parent: Node | null | string;
        original: HTMLElement;
    };
    animation: string;
    animating: boolean;
    events: Record<string, (self: _lightbox) => void>;
    listeners: Record<string, Array<(self: _lightbox) => void>>;

    constructor(props: {
        modal?: boolean;
        target?: HTMLElement;
        animation?: string;
        events?: Record<string, (self: _lightbox) => void>;
    }) {
        if (!window._lightboxList) (window as any)._lightboxList = [];

        this.modal = props.modal || false;

        this.target = document.createElement("div");
        this.el = {
            cover: "",
            main: "",
            btn: {
                close: "",
            },
        };
        this.content = {
            parent: "",
            original: document.createElement("div"),
        };

        this.animation = props.animation || "zoom";
        this.animating = false;

        this.events = props.events || {};
        this.listeners = {};

        if (this.isInLightbox(props.target)) return;

        this.init(props);
    }

    isInLightbox(target?: string | HTMLElement): boolean {
        const _target = typeof target === "string" ? document.querySelector(target) : target;
        if (!_target) return false;

        return (window as any)._lightboxList.some((lightbox: _lightbox) => lightbox.content.original === _target);
    }

    init(props: {
        modal?: boolean;
        target?: string | HTMLElement;
        events?: Record<string, (self: _lightbox) => void>;
    }) {
        // if this instance is not a modal lightbox, close prev lightboxes
        if (!this.modal && (window as any)._lightboxList) {
            (window as any)._lightboxList.forEach((item: _lightbox) => {
                item.hide();
            });
        }

        // for style
        this.style();

        this.target = document.createElement("div");
        this.target.classList.add("js-lightbox", "z-[500]", "fixed", "top-0", "left-0", "w-full", "h-full");
        document.body.appendChild(this.target);

        this.el.cover = document.createElement("div");
        this.el.cover.classList.add(
            "js-lightbox-cover",
            "z-[1]",
            "fixed",
            "top-0",
            "left-0",
            "w-full",
            "h-full",
            "bg-neutral-70",
            "bg-opacity-70",
        );
        this.target.appendChild(this.el.cover);

        this.el.main = document.createElement("div");
        this.el.main.classList.add(
            "js-lightbox-main",
            "z-[2]",
            "fixed",
            "top-0",
            "left-0",
            "w-full",
            "h-full",
            "p-4",
            "pt-16",
            "xl:p-10",
            "xl:pt-16",
            "overflow-auto",
            "scrolling-touch",
            "[.lightbox-short_&]:flex",
            "[.lightbox-short_&]:items-center",
            "[.lightbox-short_&]:justify-center",
        );
        this.target.appendChild(this.el.main);

        if (props.target) {
            const _target = typeof props.target === "string" ? document.querySelector(props.target) : props.target;

            if (_target) {
                this.content.parent = _target.parentNode;
                this.content.original = _target as HTMLElement;
            }
        }

        if (!this.content.original) {
            this.content.original = document.createElement("div");
            this.content.original.classList.add(
                "lightbox-content",
                "relative",
                "container",
                "mx-auto",
                "px-8",
                "py-12",
                "rounded-2xl",
                "bg-white",
                "shadow-[0_26px_30px_rgba(0,0,0,0.05)]",
            );
            this.content.original.innerHTML =
                '<div class="section-par text-center"><h3>Lightbox content not found!</h3></div>';
        }

        (this.el.main as HTMLElement).appendChild(this.content.original);
        this.content.original.classList.add("js-lightbox-center", "relative");

        if (!this.modal) {
            this.createCloseButton();
            document.addEventListener("keydown", this.handleEscapeKey.bind(this));
        }

        let _resizeTimer = 0;
        window.addEventListener("resize", () => {
            this.resize();

            clearTimeout(_resizeTimer);
            _resizeTimer = window.setTimeout(() => this.resize(), 100);
        });
        this.resize();

        this.emit("beforeShow");

        this.show();
        (window as any)._lightboxList.push(this);
    }

    createCloseButton() {
        this.el.btn.close = document.createElement("a");
        this.el.btn.close.classList.add(
            "js-lightbox-btn",
            "btn-close",
            "z-[3]",
            "block",
            "fixed",
            "top-0",
            "right-0",
            "p-4",
            "text-white",
            "hover:text-red-500",
            "transition-colors",
            "duration-200",
        );
        this.el.btn.close.href = "#";
        this.el.btn.close.innerHTML = `<svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M19.207 6.207a1 1 0 0 0-1.414-1.414L12 10.586 6.207 4.793a1 1 0 0 0-1.414 1.414L10.586 12l-5.793 5.793a1 1 0 1 0 1.414 1.414L12 13.414l5.793 5.793a1 1 0 0 0 1.414-1.414L13.414 12l5.793-5.793z" fill="currentColor"/></svg>`;
        this.target.appendChild(this.el.btn.close);

        this.el.btn.close.addEventListener("click", (e: Event) => {
            e.preventDefault();
            this.hide();
        });
    }

    handleEscapeKey(event: KeyboardEvent) {
        if (!this.target) return;
        if (event.key === "Escape" || event.keyCode === 27) {
            const list = (window as any)._lightboxList;
            if (list[list.length - 1] === this) this.hide();
        }
    }

    resize() {
        const _main = this.el.main as HTMLElement;
        const _mainStyle = getComputedStyle(_main);
        const _mainHeight =
            _main.offsetHeight - (parseFloat(_mainStyle.paddingTop) + parseFloat(_mainStyle.paddingBottom));

        this.target.classList.toggle("lightbox-short", this.content.original.offsetHeight < _mainHeight);

        this.emit("resize");
    }

    show(status?: string, callback?: (self: _lightbox) => void, delay: number | null = null) {
        if (this.animating) return;
        this.animating = true;

        if (callback) callback(this);

        document.body.classList.add("lightbox-active");
        this.target.classList.add("lightbox-visible");

        if (this.animation !== "none") {
            // animate the cover
            this.el.cover.classList.add("lightbox-animate", "lightbox-fadeIn");

            // animate the main content
            this.content.original.classList.add("lightbox-animate");
            if (this.animation === "fade") this.content.original.classList.add("lightbox-fadeIn");
            if (this.animation === "fadeUp") this.content.original.classList.add("lightbox-fadeInUp");
            if (this.animation === "zoom") this.content.original.classList.add("lightbox-zoomIn");

            // animate the close btn
            this.el.btn.close.classList.add("lightbox-animate", "lightbox-fadeIn");
        }

        // get animation duration for timeout
        const _style = getComputedStyle(this.content.original);
        const _animationDuration = parseFloat(_style.animationDuration) + parseFloat(_style.animationDelay);

        setTimeout(
            () => {
                // remove the animate classes
                this.el.cover.classList.remove("lightbox-animate", "lightbox-fadeIn");
                this.content.original.classList.remove(
                    "lightbox-animate",
                    "lightbox-fadeIn",
                    "lightbox-fadeInUp",
                    "lightbox-zoomIn",
                );
                this.el.btn.close.classList.remove("lightbox-animate", "lightbox-fadeIn");

                this.animating = false;

                this.emit("afterShow");
            },
            Math.max(delay ? delay : (_animationDuration ? _animationDuration : 0) * 1000, 0),
        );
    }

    hide(status?: string, callback?: (self: _lightbox) => void, delay: number | null = null) {
        if (this.animating) return;
        this.animating = true;

        if (status === "all") {
            [...(window as any)._lightboxList].forEach((item: _lightbox) => {
                item.hide();
            });
            return;
        }

        if (callback) callback(this);

        this.emit("beforeClose");

        this.target.classList.remove("lightbox-visible");
        this.target.classList.add("lightbox-hiding");

        // animate the cover
        if (this.animation !== "none") {
            this.el.cover.classList.add("lightbox-animate", "lightbox-fadeOut");

            // animate the main content
            this.content.original.classList.add("lightbox-animate");
            if (this.animation === "fade") this.content.original.classList.add("lightbox-fadeOut");
            if (this.animation === "fadeUp") this.content.original.classList.add("lightbox-fadeOutDown");
            if (this.animation === "zoom") this.content.original.classList.add("lightbox-zoomOut");

            // animate the close btn
            this.el.btn.close.classList.add("lightbox-animate", "lightbox-fadeOut");
        }

        // get animation duration for timeout
        const _style = getComputedStyle(this.content.original);
        const _animationDuration = parseFloat(_style.animationDuration) + parseFloat(_style.animationDelay);

        setTimeout(
            () => {
                // remove the animate classes
                this.el.cover.classList.remove("lightbox-animate", "lightbox-fadeOut");
                this.content.original.classList.remove(
                    "lightbox-animate",
                    "lightbox-fadeOut",
                    "lightbox-fadeOutDown",
                    "lightbox-zoomOut",
                );
                this.el.btn.close.classList.add("lightbox-animate", "lightbox-fadeOut");

                // remove the close btn
                this.el.btn.close.remove();

                if (this.content.parent) (this.content.parent as Node).appendChild(this.content.original);
                this.content.original.classList.remove("js-lightbox-center");

                (window as any)._lightboxList = (window as any)._lightboxList.filter(
                    (lightbox: _lightbox) => lightbox !== this,
                );
                this.target.remove();

                if ((window as any)._lightboxList.length === 0) document.body.classList.remove("lightbox-active");

                this.animating = false;

                this.emit("afterClose");
            },
            Math.max(delay ? delay : (_animationDuration ? _animationDuration : 0) * 1000, 0),
        );
    }

    on(event: string, callback: (self: _lightbox) => void) {
        if (!this.listeners[event]) this.listeners[event] = [];
        this.listeners[event].push(callback);
    }

    off(event: string, callback: (self: _lightbox) => void) {
        if (!this.listeners[event]) return;
        this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
    }

    emit(event: string) {
        if (this.events[event]) this.events[event](this);

        if (!this.listeners[event]) return;
        this.listeners[event].forEach((callback) => callback(this));
    }

    static close(status?: string) {
        [...(window as any)._lightboxList].forEach((item: _lightbox, index: number, arr: _lightbox[]) => {
            if (status === "all" || index === arr.length - 1) item.hide();
        });
    }

    style() {
        let _style = document.querySelector(".js-lightbox-style");
        if (!_style) {
            _style = document.createElement("style");
            _style.classList.add("js-lightbox-style");
            document.head.appendChild(_style);

            _style.innerHTML =
                `.lightbox-active { overflow: hidden; }` +
                `.scrolling-touch { overflow-scrolling: touch; -webkit-overflow-scrolling: touch; }` +
                `.lightbox-animate { animation-duration: 0.5s; animation-timing-function: cubic-bezier(0.5, 0, 0, 1.25); animation-fill-mode: forwards; }` +
                `.lightbox-fadeIn { animation-name: lightBoxFadeIn; animation-timing-function: ease; }` +
                `.lightbox-fadeOut { animation-name: lightBoxFadeOut; animation-timing-function: ease; }` +
                `.lightbox-fadeInUp { animation-name: lightBoxFadeInUp; }` +
                `.lightbox-fadeOutDown { animation-name: lightBoxFadeOutDown; }` +
                `.lightbox-zoomIn { animation-name: lightBoxZoomIn; }` +
                `.lightbox-zoomOut { animation-name: lightBoxZoomOut; }` +
                `@keyframes lightBoxFadeIn { 0% { opacity: 0; } 100% { opacity: 1; }}` +
                `@keyframes lightBoxFadeOut { 0% { opacity: 1; } 100% { opacity: 0; }}` +
                `@keyframes lightBoxFadeInUp { 0% { opacity: 0; transform: translate3d(0, 100%, 0); } 100% { opacity: 1; transform: translateZ(0); }}` +
                `@keyframes lightBoxFadeOutDown { 0% { opacity: 1; } 100% { opacity: 0; transform: translate3d(0, 100%, 0); }}` +
                `@keyframes lightBoxZoomIn { 0% { opacity: 0; transform: scale(0.8); } 100% { opacity: 1; transform: scale(1); }}` +
                `@keyframes lightBoxZoomOut { 0% { opacity: 1; transform: scale(1); } 100% { opacity: 0; transform: scale(0.8); }}`;
        }
    }
}
